<template>
  <div class="min-h-screen px-4 py-6 text-white font-sans mb-20 md:mb-0">
    <div class="max-w-7xl mx-auto space-y-6">
      <header
        class="rounded-2xl bg-gradient-to-r from-[#141c1a] via-[#0f1517] to-[#141c1a] px-5 py-4 ring-1 ring-[#334D44]/70 shadow-lg flex flex-col md:flex-row items-center justify-between gap-4"
      >
        <div class="space-y-1 w-full md:w-auto">
          <div class="flex items-center gap-3">
            <div
              class="h-10 w-10 rounded-2xl bg-[#1a2220] flex items-center justify-center ring-1 ring-[#334D44]/60 shrink-0 shadow-inner"
            >
              <span class="text-xl">💎</span>
            </div>
            <div>
              <h1 class="text-lg md:text-xl font-extrabold tracking-tight text-white">
                تابلو معامله آنلاین
              </h1>

              <div class="flex flex-wrap items-center gap-3 mt-1.5">
                <div class="text-[11px] md:text-xs text-white/60 flex items-center gap-1">
                  <span class="opacity-70">سطح دسترسی:</span>
                  <span
                    v-if="role === 'USER'"
                    class="text-[#b9f1cf] font-bold bg-[#b9f1cf]/10 px-2 py-0.5 rounded-md"
                    >کاربر عادی</span
                  >
                  <span
                    v-else-if="role === 'COWORKER'"
                    class="text-amber-300 font-bold bg-amber-300/10 px-2 py-0.5 rounded-md"
                    >همکار</span
                  >
                  <span
                    v-else
                    class="text-rose-300 font-bold bg-rose-300/10 px-2 py-0.5 rounded-md"
                    >مدیریت کل</span
                  >
                </div>

                <div
                  v-if="role === 'ADMIN'"
                  class="flex items-center gap-1 bg-[#000]/30 p-1 rounded-lg ring-1 ring-white/10"
                >
                  <button
                    @click="adminViewMode = 'USER'"
                    class="px-2 py-0.5 text-[10px] rounded transition-all"
                    :class="
                      adminViewMode === 'USER'
                        ? 'bg-[#b9f1cf] text-[#0a1f14] font-bold shadow-sm'
                        : 'text-white/50 hover:text-white'
                    "
                  >
                    کاربر
                  </button>
                  <button
                    @click="adminViewMode = 'COWORKER'"
                    class="px-2 py-0.5 text-[10px] rounded transition-all"
                    :class="
                      adminViewMode === 'COWORKER'
                        ? 'bg-amber-400 text-[#0a1f14] font-bold shadow-sm'
                        : 'text-white/50 hover:text-white'
                    "
                  >
                    همکار
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col items-end gap-1 w-full md:w-auto">
          <div
            class="inline-flex items-center gap-2 rounded-full bg-[#141c1a] px-3 py-1.5 text-[11px] ring-1 ring-[#334D44]/60 shadow-lg"
          >
            <span class="relative flex h-2.5 w-2.5">
              <span
                v-if="connected"
                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
              ></span>
              <span
                class="relative inline-flex rounded-full h-2.5 w-2.5"
                :class="connected ? 'bg-emerald-500' : 'bg-rose-500'"
              ></span>
            </span>
            <span class="font-medium text-white/90">{{
              connected ? "اتصال برقرار است" : "در حال تلاش برای اتصال…"
            }}</span>
          </div>

          <div class="text-[10px] text-white/40 font-mono dir-ltr">
            Last Update: {{ lastUpdateLabel }}
          </div>
        </div>
      </header>

      <section class="grid grid-cols-12 gap-5">
        <div class="col-span-12 lg:col-span-8 space-y-4">
          <div
            class="rounded-3xl bg-[#101617] p-1 ring-1 ring-[#334D44]/60 shadow-2xl relative overflow-hidden"
          >
            <div
              v-if="role === 'ADMIN'"
              class="h-1 w-full bg-gradient-to-r"
              :class="
                adminViewMode === 'COWORKER'
                  ? 'from-amber-500/50 via-amber-400 to-amber-500/50'
                  : 'from-emerald-500/50 via-emerald-400 to-emerald-500/50'
              "
            ></div>

            <div class="p-3 space-y-4">
              <div
                v-for="group in groupedCoinsActive"
                :key="group.id"
                class="rounded-2xl bg-[#141c1a]/50 ring-1 ring-white/5 overflow-hidden"
              >
                <div
                  class="px-4 py-3 bg-white/5 flex items-center gap-2 border-b border-white/5"
                >
                  <div class="w-1.5 h-1.5 rounded-full bg-[#b9f1cf]"></div>
                  <span class="text-xs font-bold text-white/90">{{ group.title }}</span>
                </div>

                <div class="divide-y divide-white/5">
                  <div
                    v-for="row in group.items"
                    :key="row.key"
                    class="group px-4 py-3 flex items-center justify-between transition-colors hover:bg-white/[0.02]"
                  >
                    <div class="flex flex-col">
                      <span
                        class="text-sm font-bold text-white/90 group-hover:text-white transition"
                        >{{ row.title }}</span
                      >
                      <span
                        v-if="role === 'ADMIN'"
                        class="text-[9px] text-white/20 font-mono mt-0.5"
                        >{{ row.key }}</span
                      >
                    </div>

                    <div class="flex items-center gap-3 md:gap-6">
                      <div class="text-right hidden sm:block">
                        <span class="text-[10px] text-white/40 block mb-0.5"
                          >فروش به مشتری</span
                        >
                        <div class="flex items-center gap-1">
                          <span
                            class="font-bold text-lg text-emerald-400 tracking-tight"
                            >{{ nfmt(row.buy) }}</span
                          >
                          <span class="text-[9px] text-emerald-400/60">ت</span>
                        </div>
                      </div>

                      <div class="text-right hidden sm:block">
                        <span class="text-[10px] text-white/40 block mb-0.5"
                          >خرید از مشتری</span
                        >
                        <div class="flex items-center gap-1">
                          <span class="font-bold text-lg text-rose-400 tracking-tight">{{
                            nfmt(row.sell)
                          }}</span>
                          <span class="text-[9px] text-rose-400/60">ت</span>
                        </div>
                      </div>

                      <div class="text-right sm:hidden">
                        <div class="flex flex-col items-end">
                          <span class="text-emerald-400 font-bold text-sm">{{
                            nfmt(row.buy)
                          }}</span>
                          <span class="text-rose-400 font-bold text-xs">{{
                            nfmt(row.sell)
                          }}</span>
                        </div>
                      </div>

                      <button
                        @click="openWalletBuy(row)"
                        :disabled="!row.buyActive && !row.sellActive"
                        class="px-4 py-2 rounded-xl text-xs font-extrabold transition-all shadow-[0_0_15px_-3px_rgba(185,241,207,0.3)] flex items-center justify-center gap-1"
                        :class="
                          !row.buyActive && !row.sellActive
                            ? 'bg-white/10 text-white/30 cursor-not-allowed shadow-none'
                            : 'bg-[#b9f1cf] text-[#0a1f14] hover:bg-[#a0ecc0] hover:scale-105 active:scale-95'
                        "
                      >
                        <span v-if="!row.buyActive && !row.sellActive">بسته</span>
                        <span v-else>معامله</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!groupedCoinsActive.length"
              class="flex flex-col items-center justify-center py-12 text-white/30 space-y-2"
            >
              <span class="text-2xl">📡</span>
              <span class="text-sm">در حال دریافت نرخ‌ها...</span>
            </div>
          </div>
        </div>

        <div class="col-span-12 lg:col-span-4 space-y-4">
          <div
            class="rounded-3xl bg-[#101617] p-5 ring-1 ring-[#334D44]/60 shadow-2xl sticky top-4"
          >
            <div class="text-sm font-bold mb-4 text-white/90 flex items-center gap-2">
              <span class="text-lg">💵</span>
              <span>نرخ‌های مرجع</span>
            </div>

            <div class="space-y-3">
              <div
                v-for="p in fx"
                :key="p.title"
                class="rounded-2xl bg-white/5 border border-white/5 px-4 py-3 flex items-center justify-between hover:border-white/10 transition"
              >
                <span class="text-xs font-medium text-white/70">{{ p.title }}</span>
                <div class="flex items-center gap-1">
                  <span class="text-red-500 font-bold text-white">{{
                    nfmt(p.price)
                  }}</span>
                  <span class="text-[10px] text-white/30">تومان</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <transition name="modal">
        <div
          v-if="walletBuyModal"
          class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          @click.self="closeWalletBuy"
        >
          <div
            class="relative w-full max-w-sm rounded-[2rem] overflow-hidden bg-[#0f1417] border border-white/10 shadow-[0_0_60px_-10px_rgba(0,0,0,0.8)] transform transition-all"
          >
            <div
              class="flex items-center justify-between px-6 py-5 border-b border-white/5 bg-[#141a1e]"
            >
              <div>
                <h2 class="text-sm font-extrabold text-white tracking-wide">
                  {{ walletBuyItem?.title }}
                </h2>
                <div class="text-[10px] text-white/40 mt-0.5">ثبت سفارش آنی</div>
              </div>
              <button
                @click="closeWalletBuy"
                class="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white transition"
              >
                ✕
              </button>
            </div>

            <div class="p-6 space-y-6">
              <div
                class="grid grid-cols-2 gap-1 bg-[#0a0e10] p-1.5 rounded-2xl border border-white/10"
              >
                <button
                  @click="walletBuyType = 'buy'"
                  :disabled="!walletBuyItem.buyActive"
                  class="py-3 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 relative overflow-hidden"
                  :class="[
                    walletBuyType === 'buy'
                      ? 'bg-[#b9f1cf] text-[#0a1f14] shadow-lg'
                      : 'text-white/40 hover:text-white hover:bg-white/5',
                    !walletBuyItem.buyActive ? 'opacity-30 cursor-not-allowed' : '',
                  ]"
                >
                  <span>خرید از ما</span>
                  <span class="text-[9px] opacity-70" v-if="walletBuyItem.buyActive"
                    >(شما می‌خرید)</span
                  >
                  <span class="text-[9px] text-rose-400 font-bold" v-else>(غیرفعال)</span>
                </button>
                <button
                  @click="walletBuyType = 'sell'"
                  :disabled="!walletBuyItem.sellActive"
                  class="py-3 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 relative overflow-hidden"
                  :class="[
                    walletBuyType === 'sell'
                      ? 'bg-rose-500 text-white shadow-lg'
                      : 'text-white/40 hover:text-white hover:bg-white/5',
                    !walletBuyItem.sellActive ? 'opacity-30 cursor-not-allowed' : '',
                  ]"
                >
                  <span>فروش به ما</span>
                  <span class="text-[9px] opacity-70" v-if="walletBuyItem.sellActive"
                    >(شما می‌فروشید)</span
                  >
                  <span class="text-[9px] text-rose-400 font-bold" v-else>(غیرفعال)</span>
                </button>
              </div>

              <div class="flex flex-col items-center justify-center py-2">
                <span class="text-xs text-white/50 mb-1">
                  {{
                    walletBuyType === "buy"
                      ? "قیمت واحد (فروش سایت)"
                      : "قیمت واحد (خرید سایت)"
                  }}
                </span>
                <div
                  class="text-2xl font-black tracking-tight"
                  :class="walletBuyType === 'buy' ? 'text-emerald-400' : 'text-rose-400'"
                >
                  {{
                    walletBuyType === "buy"
                      ? nfmt(walletBuyItem?.buy)
                      : nfmt(walletBuyItem?.sell)
                  }}
                  <span class="text-sm font-medium text-white/30">تومان</span>
                </div>
              </div>

              <div class="space-y-2">
                <label class="text-[11px] font-bold text-white/70 mr-1 block"
                  >تعداد / مقدار</label
                >
                <div class="relative group">
                  <input
                    v-model.number="walletBuyAmount"
                    type="number"
                    min="1"
                    step="0.001"
                    class="w-full rounded-2xl bg-[#0a0e10] border border-white/10 focus:border-[#b9f1cf] focus:ring-4 focus:ring-[#b9f1cf]/10 outline-none px-4 py-4 text-center text-lg font-bold text-white placeholder-white/20 transition"
                  />
                  <div
                    class="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col gap-1"
                  >
                    <button
                      @click="walletBuyAmount++"
                      class="text-white/30 hover:text-white text-[10px]"
                    >
                      ▲
                    </button>
                    <button
                      @click="walletBuyAmount > 1 ? walletBuyAmount-- : null"
                      class="text-white/30 hover:text-white text-[10px]"
                    >
                      ▼
                    </button>
                  </div>
                </div>
              </div>

              <div
                class="rounded-2xl bg-[#141a1e] border border-white/5 p-4 flex items-center justify-between"
              >
                <span class="text-xs text-white/60">مبلغ نهایی تراکنش</span>
                <div class="text-right">
                  <div class="text-base font-bold text-white">
                    {{ nfmt(calculateTotal) }}
                  </div>
                  <div class="text-[10px] text-white/30">تومان</div>
                </div>
              </div>

              <button
                @click="submitWalletBuy"
                :disabled="isSubmitting"
                class="w-full py-4 rounded-2xl font-bold text-sm active:scale-[.98] transition-all shadow-xl flex items-center justify-center gap-2 relative overflow-hidden"
                :class="[
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : '',
                  walletBuyType === 'buy'
                    ? 'bg-[#b9f1cf] text-[#0a1f14] hover:bg-[#96e8b6] shadow-emerald-400/20'
                    : 'bg-rose-500 text-white hover:bg-rose-600 shadow-rose-500/20',
                ]"
              >
                <span
                  v-if="isSubmitting"
                  class="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"
                ></span>
                <span v-else>
                  {{ walletBuyType === "buy" ? "پرداخت و خرید" : "تایید فروش" }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
/* eslint-disable */
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import { io } from "socket.io-client";
import Swal from "sweetalert2";

export default {
  name: "PriceBoard",
  setup() {
    const API = process.env.VUE_APP_API_URL;
    const WS = process.env.VUE_APP_API_WS;

    const connected = ref(false);
    const boardUser = ref({ coins: {}, mazane: 0, gold18: null });
    const boardCoworker = ref({ coins: {}, mazane: 0, gold18: null });
    const role = ref("USER");
    const adminViewMode = ref("USER");
    const lastUpdate = ref(null);
    const isSubmitting = ref(false);
    let socket = null;

    onMounted(async () => {
      await loadProfile();

      try {
        socket = io(WS, { transports: ["websocket"], withCredentials: true });

        socket.on("connect", () => {
          connected.value = true;
        });

        socket.on("disconnect", () => {
          connected.value = false;
        });

        socket.on("prices:init", (data) => {
          updateBoards(data);
        });

        socket.on("prices:update", (data) => {
          updateBoards(data);
        });
      } catch (err) {
        console.error("Socket Error:", err);
      }
    });

    onBeforeUnmount(() => {
      if (socket) socket.disconnect();
    });

    function updateBoards(data) {
      // لاگ برای دیباگ، میتونی بعدا پاک کنی
      // console.log("🔥 RAW DATA:", data);

      if (data.user) boardUser.value = data.user;
      if (data.coworker) boardCoworker.value = data.coworker;
      lastUpdate.value = new Date();
    }

    async function loadProfile() {
      try {
        const r = await axios.get(`${API}/user/profile`, { withCredentials: true });
        role.value = r?.data?.user?.role || "USER";
        if (role.value === "ADMIN") adminViewMode.value = "USER";
      } catch {
        role.value = "USER";
      }
    }

    const activeBoard = computed(() => {
      if (role.value === "USER") return boardUser.value;
      if (role.value === "COWORKER") return boardCoworker.value;
      if (role.value === "ADMIN") {
        return adminViewMode.value === "COWORKER" ? boardCoworker.value : boardUser.value;
      }
      return boardUser.value;
    });

    const fx = computed(() => {
      const c = activeBoard.value?.coins || {};
      return [
        { title: "دلار آزاد", price: c.Dollar?.sell || 0 },
        { title: "درهم امارات", price: c.Derham?.sell || 0 },
      ];
    });

    const groupedCoinsActive = computed(() => mapCoins(activeBoard.value));

    const lastUpdateLabel = computed(() => {
      if (!lastUpdate.value) return "---";
      return lastUpdate.value.toLocaleTimeString("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    });

    const calculateTotal = computed(() => {
      if (!walletBuyItem.value) return 0;

      const unitPrice =
        walletBuyType.value === "buy"
          ? walletBuyItem.value.buy // کاربر میخرد -> قیمت buy
          : walletBuyItem.value.sell; // کاربر میفروشد -> قیمت sell
      return Math.round(unitPrice * (walletBuyAmount.value || 0));
    });

    function nfmt(n) {
      return new Intl.NumberFormat("fa-IR").format(Number(n || 0));
    }

    function mapWalletKey(key) {
      const map = {
        ImamBanki: "SekehEmam",
        TamamBanki: "SekehTamam",
        NimBanki: "SekehNim",
        RobBanki: "SekehRob",
        Gerami: "SekehGerami",
        TamamNonBanki: "SekehTamamGheirBanki",
        NimNonBanki: "SekehNimGheirBanki",
        RobNonBanki: "SekehRobGheirBanki",
        gold18: "YekGram18",
        mazane: "mazane",
        Dollar: "Dollar",
        Derham: "Derham",
      };
      return map[key] || key;
    }

    function coinName(key) {
      const map = {
        ImamBanki: "سکه امامی بانکی",
        TamamBanki: "سکه تمام بانکی",
        NimBanki: "نیم سکه بانکی",
        RobBanki: "ربع سکه بانکی",
        Gerami: "سکه گرمی",
        TamamNonBanki: "سکه تمام غیر بانکی",
        NimNonBanki: "نیم سکه غیر بانکی",
        RobNonBanki: "ربع سکه غیر بانکی",
        Dollar: "دلار آزاد",
        Derham: "درهم امارات",
        gold18: "طلای ۱۸ عیار",
        mazane: "مظنه",
      };
      return map[key] || key;
    }

    function mapCoins(board) {
      if (!board || !board.coins) return [];
      const coins = board.coins;

      const groups = [
        { id: "gold18", title: "طلای آبشده و ۱۸ عیار", keys: ["gold18", "mazane"] },
        {
          id: "bank",
          title: "سکه بانکی (تضمینی)",
          keys: ["ImamBanki", "TamamBanki", "NimBanki", "RobBanki"],
        },
        {
          id: "nb",
          title: "سکه غیر بانکی",
          keys: ["TamamNonBanki", "NimNonBanki", "RobNonBanki"],
        },
        { id: "gerami", title: "سکه گرمی", keys: ["Gerami"] },
      ];

      const result = [];

      groups.forEach((g) => {
        const items = [];
        g.keys.forEach((k) => {
          let data = null;
          let source = null;

          if (k === "gold18" && board.gold18) source = board.gold18;
          else if (k === "mazane" && board.mazane)
            source = {
              buy: board.mazane,
              sell: board.mazane,
              buy_active: true,
              sell_active: true,
            };
          else if (coins[k]) source = coins[k];

          if (source) {
            const isBuyActive = source.buy_active !== false;
            const isSellActive = source.sell_active !== false;

            data = {
              key: k,
              title: coinName(k),
              buy: source.buy,
              sell: source.sell,
              buyActive: isBuyActive,
              sellActive: isSellActive,
            };
          }

          if (data) items.push(data);
        });

        if (items.length > 0) {
          result.push({ ...g, items });
        }
      });

      return result;
    }

    const walletBuyModal = ref(false);
    const walletBuyItem = ref(null);
    const walletBuyAmount = ref(1);
    const walletBuyType = ref("buy");

    function openWalletBuy(row) {
      walletBuyItem.value = row;
      walletBuyAmount.value = 1;

      if (row.buyActive) {
        walletBuyType.value = "buy";
      } else if (row.sellActive) {
        walletBuyType.value = "sell";
      } else {
        walletBuyType.value = "buy";
      }

      walletBuyModal.value = true;
    }

    function closeWalletBuy() {
      if (!isSubmitting.value) {
        walletBuyModal.value = false;
      }
    }

    async function submitWalletBuy() {
      if (!walletBuyItem.value) return;
      if (walletBuyAmount.value <= 0) {
        Swal.fire({
          icon: "warning",
          title: "خطا",
          text: "مقدار وارد شده نامعتبر است",
          background: "#0f1517",
          color: "#fff",
        });
        return;
      }

      if (walletBuyType.value === "buy" && !walletBuyItem.value.buyActive) {
        Swal.fire({
          icon: "error",
          title: "غیرفعال",
          text: "امکان خرید این ارز در حال حاضر وجود ندارد.",
          background: "#0f1517",
          color: "#fff",
        });
        return;
      }
      if (walletBuyType.value === "sell" && !walletBuyItem.value.sellActive) {
        Swal.fire({
          icon: "error",
          title: "غیرفعال",
          text: "امکان فروش این ارز در حال حاضر وجود ندارد.",
          background: "#0f1517",
          color: "#fff",
        });
        return;
      }

      isSubmitting.value = true;

      try {
        const payload = {
          item: mapWalletKey(walletBuyItem.value.key),
          amount: Number(walletBuyAmount.value),
          type: walletBuyType.value,
        };

        const res = await axios.post(`${API}/user/wallet/order`, payload, {
          withCredentials: true,
        });

        walletBuyModal.value = false;

        Swal.fire({
          icon: "success",
          title: "تراکنش موفق",
          text: res.data.msg || "سفارش شما با موفقیت ثبت شد.",
          confirmButtonColor: "#00c897",
          confirmButtonText: "بسیار عالی",
          background: "#0f1517",
          color: "#fff",
          timer: 3000,
          timerProgressBar: true,
        });
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "خطا در برقراری ارتباط با سرور";

        Swal.fire({
          icon: "error",
          title: "خطا در تراکنش",
          text: msg,
          confirmButtonColor: "#ff6b6b",
          confirmButtonText: "بررسی می‌کنم",
          background: "#0f1517",
          color: "#fff",
        });
      } finally {
        isSubmitting.value = false;
      }
    }

    return {
      connected,
      role,
      adminViewMode,
      lastUpdateLabel,
      fx,
      groupedCoinsActive,
      nfmt,
      calculateTotal,
      isSubmitting,
      walletBuyModal,
      walletBuyItem,
      walletBuyAmount,
      walletBuyType,
      openWalletBuy,
      closeWalletBuy,
      submitWalletBuy,
    };
  },
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-enter-from > div {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}
.modal-leave-to > div {
  transform: scale(0.95);
  opacity: 0;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
